import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Alert, Typography } from "@iluvatar/ui-kit";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";

import { useMatchedUser } from "@/hooks/useMatchedUser";

import {
  IFileReference,
  Message,
  ProfileDetails,
} from "@iluvatar/global/src/typings";
import { hasMessageText } from "@iluvatar/global/src/utilities/messages";

import { ChatComposer } from "@/components/ChatComposer";
import { ChatMessageThread } from "@/components/ChatMessage";
import { FlexSpacer } from "@/components/FlexSpacer";
import { Form } from "@/components/Form";
import { ChatFileSelectField } from "@/components/Form/Fields/File/chat-files";
import { MessageContainer } from "@/components/MessageContainer";
import { useNotificationDismissal } from "@/hooks/notificationDismissal";
import { useChat } from "@/hooks/useChat";
import { useChatScroll } from "@/hooks/useChatScroll";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useRecentMessages } from "@/hooks/useRecentMessages";
import DefaultLayout from "@/layout/default-page";
import uploadService from "@/services/upload.service";
import { cn } from "@/utilities";

function getProfileAv(
  prof?: ProfileDetails | undefined,
): IFileReference | undefined {
  if (prof?.imageIds[0]) {
    return (prof?.imagesById || {})[prof?.imageIds[0]];
  }

  return;
}

export const Route = createFileRoute("/chat/$chatId")({
  component: UserChat,
});

function UserChat() {
  const params: { chatId: string } = useParams({ strict: false });
  const [readyChatId, setReadyChatId] = useState<string>();
  const onMessagesReady = useCallback(
    () => setReadyChatId(params.chatId),
    [params.chatId],
  );
  useNotificationDismissal("messages", params.chatId);

  const ref = useRef<HTMLTextAreaElement>(null);
  const imageSelectRef = useRef<HTMLInputElement>(null);
  const sending = useRef(false);
  const voiceDraft = useRef(false);
  const onVoiceDraftChange = useCallback((active: boolean) => {
    voiceDraft.current = active;
  }, []);

  const { user } = useCurrentUser();
  const { chat, lastMessageId, error, state, chatApi } = useChat(params.chatId);
  const { data: initialMessages, isLoadingMessages } = useRecentMessages(
    params.chatId,
  );
  const partnerId =
    chat?.sourceUID === user?.uid ? chat?.targetUID : chat?.sourceUID;

  const { data: partnerProfile } = useMatchedUser(partnerId, chat?.communityId);

  const [loading, disableInput] = useState(false);
  const [sendError, setSendError] = useState<string>();

  const { pageRef, messagesRef } = useChatScroll(
    params.chatId,
    Boolean(chat && !chat.inactive),
  );

  if (chat?.inactive) {
    return (
      <DefaultLayout
        className={
          "profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px]"
        }
        style={{
          marginTop: 0,
          paddingTop: "var(--nav-height)",
        }}
      >
        <MessageContainer>
          <Typography variant="h2">
            This chat is inactive and cannot be accessed.
          </Typography>
        </MessageContainer>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout
      ref={pageRef}
      className={
        "profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px]"
      }
      style={{
        marginTop: 0,
        paddingTop: "var(--nav-height)",
        paddingBottom: 0,
        overflowX: "unset",
        minHeight: "calc(100vh + 1px)",
      }}
    >
      {chat && <FlexSpacer />}
      {Boolean(error) && (
        <MessageContainer>
          <Typography variant="h2">
            Looks like you got a digital bo-bo.
          </Typography>
          <Typography variant="caption" className="[margin-top:16px]">
            {(error as any)?.message || JSON.stringify(error)}
          </Typography>
        </MessageContainer>
      )}
      {chat && (
        <div
          ref={messagesRef}
          style={{
            visibility:
              lastMessageId && readyChatId !== params.chatId
                ? "hidden"
                : undefined,
          }}
        >
          {isLoadingMessages && (
            <LoadingIndicator aria-label="Loading messages" />
          )}
          {lastMessageId && !isLoadingMessages && (
            <ChatMessageThread
              key={params.chatId}
              chatId={params.chatId}
              lastMessageId={lastMessageId}
              initialMessages={initialMessages}
              communityId={chat.communityId}
              uid={user?.uid}
              ownImage={user && getProfileAv(user.profiles[chat.communityId])}
              partnerImage={getProfileAv(partnerProfile?.profile)}
              onReady={onMessagesReady}
              onRemove={(messageId) =>
                chatApi.removeMessage(params.chatId, messageId)
              }
            />
          )}
        </div>
      )}
      {state === "success" && !error && !lastMessageId && (
        <>
          <FlexSpacer />
          <div
            className={cn(
              "my-8 flex flex-1 justify-center center items-center",
              "w-[100%] min-h-[80px]",
            )}
          >
            <Typography variant="h3">No Messages Yet</Typography>
            {/* <Typography variant="caption">Be bold! Be shy! Be you!</Typography> */}
          </div>
          <FlexSpacer />
        </>
      )}
      {chat && (
        <>
          <div
            className={
              "chat-action-bar [background-color:var(--color-background-default)] [padding:16px] [padding-bottom:max(16px,_env(safe-area-inset-bottom,_0px))] [@media(max-width:_780px)]:[padding:8px_max(8px,_env(safe-area-inset-right,_0px))_max(8px,_env(safe-area-inset-bottom,_0px))_max(8px,_env(safe-area-inset-left,_0px))] profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px] profile-container--input sticky bottom-0 px-0"
            }
          >
            {sendError && (
              <Alert tone="danger">
                {sendError} Your draft and attachments have been kept. Try
                sending again.
              </Alert>
            )}
            <Form
              persistLocal
              id={`chat-${user?.uid}-${params.chatId}`}
              storagePath={`conversation/${params.chatId}`}
              disabled={loading}
              initialValues={{
                text: "",
                "image-select": [] as IFileReference[],
              }}
              onSubmit={async (value, helpers) => {
                const text = hasMessageText(value.text) ? value.text : "";
                if (
                  sending.current ||
                  voiceDraft.current ||
                  (!text && !value["image-select"]?.length)
                ) {
                  return;
                }
                sending.current = true;
                try {
                  disableInput(true);
                  setSendError(undefined);

                  await uploadService.uploadAllFormFiles(
                    `chat-${user?.uid}-${params.chatId}`,
                    value,
                  );
                  const msg: Partial<Message> = {
                    message: text,
                    mediaRef: value["image-select"],
                  };
                  if (!text && !value["image-select"]?.length) {
                    return;
                  }
                  await chatApi.addMessage(params.chatId, msg);
                  await uploadService.removeTasks(
                    `chat-${user?.uid}-${params.chatId}`,
                  );
                  await helpers.resetForm();
                  setTimeout(() => {
                    if (ref.current) {
                      ref.current.focus();
                    }
                  }, 10);
                } catch (err) {
                  setSendError(
                    err instanceof Error
                      ? err.message
                      : "The message could not be sent.",
                  );
                } finally {
                  sending.current = false;
                  disableInput(false);
                }
              }}
              className="flex flex-row flex-wrap relative"
            >
              <ChatFileSelectField
                ref={imageSelectRef}
                multiple
                disabled={loading}
                name="image-select"
                className="hidden"
              />
              <ChatComposer
                key={params.chatId}
                ref={ref}
                disabled={loading}
                placeholder={`Message @${partnerProfile?.displayName}`}
                onAttach={() => imageSelectRef.current?.click()}
                onVoiceDraftChange={onVoiceDraftChange}
              />
            </Form>
          </div>
          {!state && (
            <div className="fixed bottom-2 left-1/2 -translate-x-1/2">
              <LoadingIndicator aria-label="Loading chat" />
            </div>
          )}
          <div
            className={
              "chat-scroll-anchor [#scroller_&]:[margin-top:-1px] [#scroller_&]:[height:1px] [#scroller_&]:[overflow-anchor:auto]"
            }
          />
        </>
      )}
    </DefaultLayout>
  );
}
